import { type ReactNode, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ToastDetail } from '../lib/toast';
import {
  STATUS_COLOR, STATUS_LABEL, PRIORITY_COLOR, PRIORITY_LABEL, TYPE_ICON, TYPE_LABEL, TYPE_BADGE,
  enumLabel,
} from '../lib/format';
import { Icon } from '../lib/icons';

/**
 * Uniform status chip. Pass `blocked` wherever the ticket is in scope so a
 * blocked ticket is never shown as a plain status — it renders a red
 * "Blocked" chip right after the status chip.
 */
export function StatusBadge({ status, blocked }: { status: string; blocked?: boolean }) {
  return (
    <>
      <span className={`chip ${STATUS_COLOR[status] ?? 'bg-slate-100 text-slate-700'}`}>
        {STATUS_LABEL[status] ?? enumLabel(status)}
      </span>
      {blocked && <span className="chip bg-rose-100 text-rose-700 font-semibold">Blocked</span>}
    </>
  );
}

export function PriorityBadge({ priority }: { priority: string }) {
  return (
    <span className={`chip bg-surface2 ${PRIORITY_COLOR[priority] ?? 'text-muted'}`}>
      <Icon name="chevron-up" /> {PRIORITY_LABEL[priority] ?? priority}
    </span>
  );
}

export function TypeTag({ type, legacy, custom }: { type: string; legacy?: boolean; custom?: boolean }) {
  const tag = (
    <span className={`chip ${TYPE_BADGE[type] ?? 'bg-surface2 text-muted'}`} title={TYPE_LABEL[type] ?? type}>
      <Icon name={TYPE_ICON[type] ?? 'ticket'} /> {TYPE_LABEL[type] ?? type}
    </span>
  );
  if (!legacy && !custom) return tag;
  // Always-on "Legacy" / "Custom" badges next to the type badge, driven by the
  // isLegacy / customSystem checkboxes. whitespace-nowrap keeps the chips on one
  // line (the grid's type column sizes to fit them; see COL_WIDTH.type).
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      {tag}
      {legacy && <span className="chip shrink-0 bg-amber-100 text-amber-700 text-[10px]" title="Legacy (pre-Tracx) item">Legacy</span>}
      {custom && <span className="chip shrink-0 bg-purple-100 text-purple-700 text-[10px]" title="Custom system — lives in a non-Tracx system">Custom</span>}
    </span>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`skeleton animate rounded-lg ${className}`} />;
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 text-muted">
      <div className="text-4xl mb-3 opacity-50">∅</div>
      <div className="font-medium text-ink">{title}</div>
      {hint && <div className="text-sm mt-1">{hint}</div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide, dismissible = true, expandable = false }: {
  open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean;
  /** When false, backdrop click + Escape don't close it — only the X (or an
   *  explicit Cancel/Save inside) can. Use for data-entry modals so an accidental
   *  outside click never discards progress. */
  dismissible?: boolean;
  /** Show a maximize toggle that grows the dialog to (near) full-screen — for
   *  content-heavy dialogs (e.g. Create ticket) that feel cramped at the default
   *  width. Resets to the default size each time the modal re-opens. */
  expandable?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && dismissible) onClose(); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, dismissible]);
  // Always start at the default size when the dialog opens.
  useEffect(() => { if (!open) setExpanded(false); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-black/40" onClick={dismissible ? onClose : undefined}>
      <motion.div
        initial={{ opacity: 0, y: -8, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className={`card shadow-pop w-full flex flex-col ${
          expanded
            ? 'max-w-none mt-0 h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)]'
            : `${wide ? 'max-w-3xl' : 'max-w-lg'} mt-8 max-h-[calc(100vh-5rem)]`
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-border shrink-0">
          <h3 className="font-semibold">{title}</h3>
          <div className="flex items-center gap-1">
            {expandable && (
              <button
                type="button"
                className="text-muted hover:text-ink p-1"
                onClick={() => setExpanded((e) => !e)}
                title={expanded ? 'Restore default size' : 'Expand to full screen'}
                aria-label={expanded ? 'Restore default size' : 'Expand to full screen'}
              >
                <Icon name={expanded ? 'compress' : 'expand'} />
              </button>
            )}
            <button className="text-muted hover:text-ink text-lg leading-none p-1" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
          </div>
        </div>
        {/* Body scrolls when content is taller than the viewport, so action
            buttons at the bottom of a modal stay reachable. flex-1/min-h-0 lets it
            fill the dialog when expanded (keeps a sticky footer at the bottom). */}
        <div className="p-5 overflow-y-auto flex-1 min-h-0">{children}</div>
      </motion.div>
    </div>
  );
}

/** Subtle page-transition wrapper (15.2: 120–180ms). */
export function FadeIn({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

const TOAST_STYLE: Record<string, string> = {
  success: 'bg-emerald-600', error: 'bg-rose-600', info: 'bg-slate-700',
};

/** Global transient toasts. Listens for window 'atlas-toast' events. */
export function Toaster() {
  const [items, setItems] = useState<ToastDetail[]>([]);
  useEffect(() => {
    const onToast = (e: Event) => {
      const detail = (e as CustomEvent<ToastDetail>).detail;
      setItems((cur) => [...cur, detail]);
      setTimeout(() => setItems((cur) => cur.filter((t) => t.id !== detail.id)), 2600);
    };
    window.addEventListener('atlas-toast', onToast);
    return () => window.removeEventListener('atlas-toast', onToast);
  }, []);
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 items-end">
      <AnimatePresence>
        {items.map((t) => (
          <motion.div key={t.id}
            initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className={`text-white text-sm px-3.5 py-2 rounded-lg shadow-pop ${TOAST_STYLE[t.kind] ?? TOAST_STYLE.info}`}>
            {t.kind === 'success' ? <Icon name="check" /> : t.kind === 'error' ? <Icon name="warning" /> : null}{t.kind === 'success' || t.kind === 'error' ? ' ' : ''}{t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

export function MiniBar({ data, max }: { data: { key: string; count: number; color?: string }[]; max?: number }) {
  const top = max ?? Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="space-y-1.5">
      {data.map((d) => (
        <div key={d.key} className="flex items-center gap-2 text-sm">
          <div className="w-32 truncate text-muted">{STATUS_LABEL[d.key] ?? enumLabel(d.key)}</div>
          <div className="flex-1 h-2.5 rounded-full bg-surface2 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${(d.count / top) * 100}%`, background: d.color ?? 'rgb(var(--brand))' }}
            />
          </div>
          <div className="w-8 text-right tabular-nums">{d.count}</div>
        </div>
      ))}
    </div>
  );
}
